export interface ApiError {
  detail?: string;
  status: number;
  title: string;
}

export class ApiFailure extends Error {
  readonly errors: ApiError[];
  readonly status: number;

  constructor(status: number, errors: ApiError[]) {
    super(errors[0]?.detail || errors[0]?.title || "The request failed");
    this.name = "ApiFailure";
    this.status = status;
    this.errors = errors;
  }
}

/**
 * The single seam between the interface and the API.
 *
 * Paths are relative, so the session cookie stays first-party. Vite proxies
 * `/app` to the API in development; CloudFront carries the same behavior in
 * deployment. No token ever reaches this code.
 */
export async function apiFetch<T>(
  path: string,
  init: RequestInit = {},
): Promise<T> {
  const response = await fetch(path, {
    credentials: "same-origin",
    ...init,
    headers: {
      Accept: "application/json",
      ...(init.body ? { "Content-Type": "application/json" } : {}),
      ...init.headers,
    },
  });

  if (response.status === 204) return undefined as T;

  const payload = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw new ApiFailure(
      response.status,
      (payload as { errors?: ApiError[] }).errors || [
        { status: response.status, title: response.statusText },
      ],
    );
  }

  return (payload as { data: T }).data;
}

export function apiPost<T>(path: string, body?: unknown): Promise<T> {
  return apiFetch<T>(path, {
    method: "POST",
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });
}

export function signIn(returnTo = window.location.pathname): void {
  window.location.href = `/auth/login?returnTo=${encodeURIComponent(returnTo)}`;
}

export function signOut(): void {
  window.location.href = "/auth/logout";
}
